import type { ReactElement } from 'react';

import type { AdminMetrics, MetricsPeriodKey } from '@/admin/adminApi';
import { DonutChart, StackedBarChart } from '@/ui/charts';
import { Button, Card } from '@/ui/kit';

import { dayLabel, ENTRY_KINDS, entryPath, periodShort } from './dashboardModel';
import { ReadBlock } from './ReadBlock';
import type { AdminRead } from './useAdminRead';

/**
 * Activite des 30 derniers jours (barres empilees par nature) et repartition des
 * saisies de la periode (anneau). Variante large seulement : sous 600 px ces deux
 * blocs ne sont **pas rendus** — ils lisent la meme reponse que les tuiles, donc
 * les masquer n'economise aucune requete, mais ne pas les monter evite de
 * dessiner ce qu'on ne regarde pas.
 *
 * Les graphiques viennent du lot BO-03 (alternative textuelle, couleurs du theme,
 * `null` n'est pas zero) : ici on leur donne les series telles que servies.
 * **Aucun total n'est calcule** : le centre de l'anneau est `entries.current`, et
 * la legende ecrit le compte de chaque nature, pas un pourcentage derive.
 */

export const ActivityCard = ({
  metrics,
  onRetry,
}: {
  readonly metrics: AdminRead<AdminMetrics>;
  readonly onRetry: () => void;
}): ReactElement => {
  const statisticsPath = entryPath('stats');

  return (
    <Card
      title="Activité des 30 derniers jours"
      subtitle="Saisies reçues par le serveur, par nature"
      actions={
        statisticsPath === null ? undefined : (
          <Button variant="ghost" size="S" to={statisticsPath}>
            Statistiques
          </Button>
        )
      }
    >
      <ReadBlock state={metrics} label="Activité" onRetry={onRetry} skeletonHeight={240}>
        {({ series }) => (
          <StackedBarChart
            label="Saisies reçues par jour, par nature, sur les 30 derniers jours"
            categories={series.days.map(dayLabel)}
            categoryHeader="Jour"
            series={ENTRY_KINDS.map(({ kind, label }) => ({ label, values: series.entriesByKind[kind] }))}
          />
        )}
      </ReadBlock>
    </Card>
  );
};

export const KindsCard = ({
  metrics,
  period,
  onRetry,
}: {
  readonly metrics: AdminRead<AdminMetrics>;
  readonly period: MetricsPeriodKey;
  readonly onRetry: () => void;
}): ReactElement => (
  <Card title={`Saisies par nature · ${periodShort(period)}`}>
    <ReadBlock state={metrics} label="Répartition des saisies" onRetry={onRetry} skeletonHeight={150}>
      {({ entries }) => {
        const counts = new Map(entries.byKind.map((entry) => [entry.kind, entry.count]));

        return (
          <DonutChart
            label={`Saisies par nature, ${periodShort(period)}`}
            categoryHeader="Nature"
            total={entries.current}
            totalCaption="saisies"
            segments={ENTRY_KINDS.map(({ kind, label }) => ({ label, value: counts.get(kind) ?? null }))}
          />
        );
      }}
    </ReadBlock>
  </Card>
);
